import { AlarmService } from './../../services/alarm.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import {
  AfterViewInit,
  Component,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { BaseFormComponent } from '@components/_base-form/base-form.component';
import {
  UntypedFormBuilder,
  UntypedFormArray,
  FormControl,
  UntypedFormGroup,
  FormArray,
  FormGroup,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { LogService } from '@services/log.service';
import { UserService } from '@services/user.service';
import { UserDropdownDTO } from '@interfaces/user-dropdown-list';
import { TankSetupService } from '@services/tank-setup.service';
import { BehaviorSubject, map, of, catchError, Observable } from 'rxjs';
import { DropDownDTO } from '@interfaces/drop-down.interface';
import {
  IAlarmOption,
  IEmailAlertFormItem,
  IEmailAlertFormResponse,
} from '@interfaces/email-alerts-form';
import { OrganizationService } from '@services/organization.service';
import { LocationService } from '@services/location.service';

const emailRegexPattern = '^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$';
@Component({
  selector: 'app-alarm-email-form',
  templateUrl: './alarm-email-form.component.html',
  styleUrls: ['./alarm-email-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlarmEmailFormComponent
  extends BaseFormComponent<IEmailAlertFormResponse>
  implements AfterViewInit, OnChanges
{
  @Input() alarmType: AlarmTypes;
  @Input()
  set formData(value: IEmailAlertFormResponse) {
    if (!value) return;
    this.setupForm(value);
  }
  @Input() entityId: number;

  formLoaded: boolean = false;
  filterInactive: boolean = false;

  //formGroups for tracking the add by email and add by user or group
  addAsEmailFormGroup = this.formBuilder.group({});
  addAsUserOrGroupFormGroup = this.formBuilder.group({});
  inactiveEmailAlertDefinitionToggle = this.formBuilder.group({});

  //user and group list for the dropdown
  _userAndGroupListDataOriginal: UserDropdownDTO[] = [];
  _userAndGroupListDataMapped: DropDownDTO[] = [];
  userAndGroupListOptions$ = new BehaviorSubject(null);

  get userAndGroupListOptions() {
    return this._userAndGroupListDataMapped?.filter((u) => {
      //filter out emails that are already in the alarm definition list
      const usrID = u.value;
      const email = this._userAndGroupListDataOriginal?.find(
        (usr) => usr.id === usrID,
      )?.emailAddress;

      const emailAlreadyExists = this.emailAlertFormItems?.find(
        (item) => item.emailAddress?.toLowerCase() === email?.toLowerCase(),
      );
      return !emailAlreadyExists;
    });
  }

  get userAlarmDefintionsFormArray() {
    return this.formGroup.get('userAlarmDefintions') as UntypedFormArray;
  }

  get formData(): IEmailAlertFormResponse {
    return super.formData;
  }

  get disableAddUserByUserOrGroupBtn() {
    return this.userOrGroupSelectFormControl?.value < 1;
  }

  get disableAddUserByEmailBtn() {
    return (
      (this.existingEmailFormControl?.value || '').length < 1 ||
      this.existingEmailFormControl?.invalid
    );
  }

  get userOrGroupSelectFormControl(): FormControl<number> {
    return this.addAsUserOrGroupFormGroup?.get(
      'selectUserOrGroup',
    ) as FormControl<number>;
  }

  get existingEmailFormControl(): FormControl<string> {
    return this.addAsEmailFormGroup?.get(
      'existingEmail',
    ) as FormControl<string>;
  }

  get emailAlertFormItems(): IEmailAlertFormItem[] {
    return this.formData?.emailAlertFormItems;
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private logger: LogService,
    public translate: TranslateService,
    private userService: UserService,
    private changeDetectorRef: ChangeDetectorRef,
    private tankSetupService: TankSetupService,
    private organizationService: OrganizationService,
    private locationService: LocationService,
    private _alarmService: AlarmService,
  ) {
    super(formBuilder);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.alarmType) {
      throw new Error('alarmType is required');
    }
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  onFilterInactiveChange($event: MatCheckboxChange) {
    this.filterInactive = $event.checked;
  }

  onAddAlertByUserOrGroup(selectionControl: FormControl<number>) {
    if (!(selectionControl?.value > 0)) return;
    //debugger;
    //get the user or group
    const userId = selectionControl.value;
    const userOrGroup = this._userAndGroupListDataOriginal.find(
      (u) => u.id === userId,
    );

    if (!userOrGroup || this.isUserOrGroupAlreadyAdded(userOrGroup)) return;

    //splice new form item into top of array
    const newItem = this.createNewEmailAlertFormItem(userOrGroup);
    const fg = this.addItemToFormArray(newItem);
    this.emailAlertFormItems.splice(0, 0, newItem);
    this.userOrGroupSelectFormControl.setValue(0);

    //mark form group as dirty
    // wait for the form to update before marking as dirty
    this.userAndGroupListOptions$.next(this.userAndGroupListOptions);
    this.markFormGroupAsDirty(fg);
    // this.changeDetectorRef.markForCheck();
  }

  onAddAlertByEmail(userToAdd: FormControl<string>) {
    if (!userToAdd || !userToAdd.valid || !userToAdd.value) return;

    //validate the email address is NOT already listed.
    const emailAlreadyExists = this.emailAlertFormItems.some(
      (item) =>
        item.emailAddress.toLowerCase().trim() ===
        userToAdd.value.toLowerCase().trim(),
    );

    if (emailAlreadyExists) {
      this.existingEmailFormControl.setErrors({ emailAlreadyExists: true });
      return;
    }

    //splice new form item into top of array
    const newItem = this.createNewEmailAlertFormItem(userToAdd.value);

    if (newItem.userId > 0) {
      this.addEmailAlertItemToForm(newItem);
      return;
    }

    //unkown user, try to get the user Id from the email
    this.isEmailAlreadyInDb(newItem).subscribe(
      (newItem: IEmailAlertFormItem) => {
        this.addEmailAlertItemToForm(newItem);
      },
    );
  }

  addEmailAlertItemToForm(newItem: IEmailAlertFormItem) {
    const fg = this.addItemToFormArray(newItem);
    this.emailAlertFormItems.splice(0, 0, newItem);
    this.existingEmailFormControl.setValue(null);
    this.userAndGroupListOptions$.next(this.userAndGroupListOptions);
    this.markFormGroupAsDirty(fg);
  }

  undoAddNewItem(index: number) {
    this.emailAlertFormItems.splice(index, 1);
    this.userAlarmDefintionsFormArray.removeAt(index);
    this.userAndGroupListOptions$.next(this.userAndGroupListOptions);
    this.changeDetectorRef.markForCheck();
  }

  getUserNameOrEmail(item: IEmailAlertFormItem): string {
    if (!item) return '';
    return item.userName?.length > 0 ? item.userName : item.emailAddress;
  }

  private markFormGroupAsDirty(fg: UntypedFormGroup) {
    setTimeout(() => {
      fg.markAsDirty({ onlySelf: false });
      fg.markAsTouched({ onlySelf: false });
      this.changeDetectorRef.markForCheck();
    }, 500);
  }

  private createNewEmailAlertFormItem(
    userOrGroup: any | string,
  ): IEmailAlertFormItem {
    const alarmTypeName: string = AlarmTypes[this.alarmType];
    const newItem: IEmailAlertFormItem = {
      active: true,
      alarmOptions: this.getAllAlarmOptions(),
      alarmID: 0,
      emailAddress: '',
      userId: 0,
      userName: '',
      alarmTypeName: alarmTypeName,
      notificationType: 1, //email
      entityId: this.entityId,
    };

    if (typeof userOrGroup === 'string') {
      newItem.emailAddress = userOrGroup;
      newItem.userName = userOrGroup;
    } else {
      newItem.emailAddress = userOrGroup.emailAddress;
      newItem.userName = userOrGroup.name;
      newItem.userId = userOrGroup.id;
    }

    this.userAndGroupListOptions$.next(this.userAndGroupListOptions);
    return newItem;
  }

  private getAllAlarmOptions(): IAlarmOption[] {
    const alarmOptions: IAlarmOption[] = [];
    const defaultIsSelected = false;
    const alarmNames = [
      'CriticalLowAlarm',
      'LowAlarm',
      'HighAlarm',
      'CriticalHighAlarm',
      'LowTempAlarm',
      'HighTempAlarm',
      'LowBatteryAlarm',
      'FillDetect',
      'ShortFillDetect',
      'ExpectedCallAlarm',
      'VolumeAlarm',
      'ReorderAlarm',
      'SafetyStockAlarm',
      'RateChangeAlarm',
    ];
    alarmNames.forEach((alarmName) => {
      alarmOptions.push({
        alarmName: alarmName,
        isSelected: defaultIsSelected,
      });
    });
    //add the active alarm option
    alarmOptions.push({ alarmName: 'Active', isSelected: true });

    return alarmOptions;
  }

  private addItemToFormArray(
    dataElement: IEmailAlertFormItem,
  ): UntypedFormGroup {
    //add in alarmOptions
    const alarmOptions = this.formBuilder.array([]);
    dataElement.alarmOptions.forEach((ao) => {
      alarmOptions.push(this.formBuilder.group({}));
    });

    const group = this.formBuilder.group({
      alarmOptions: alarmOptions,
    });

    if (dataElement.alarmID === 0) {
      //new items are added to the top
      this.userAlarmDefintionsFormArray.insert(0, group);
    } else {
      this.userAlarmDefintionsFormArray.push(group);
    }
    this.userAndGroupListOptions$.next(this.userAndGroupListOptions);
    return group;
  }

  //users/groups are searched on demand by the picker; keep the picked row so it can be added
  onUserOrGroupPicked(user: UserDropdownDTO | null) {
    if (user && !this._userAndGroupListDataOriginal.some((u) => u.id === user.id)) {
      this._userAndGroupListDataOriginal.push(user);
    }
  }

  //hide users with an invalid email and users/groups already in the alarm definition list
  isUserOrGroupAlreadyAdded = (user: UserDropdownDTO): boolean =>
    (!user.isGroup &&
      !(user.emailAddress && new RegExp(emailRegexPattern, 'i').test(user.emailAddress))) ||
    !!this.emailAlertFormItems?.some(
      (item) => item.userId === user.id ||
        (!!user.emailAddress &&
          item.emailAddress?.toLowerCase() === user.emailAddress.toLowerCase()),
    );

  private setupForm(value: IEmailAlertFormResponse) {
    //debugger;
    this.formLoaded = false;
    const fgKey = 'userAlarmDefintions';
    if (!this.formGroup.get(fgKey))
      this.formGroup.addControl(fgKey, this.formBuilder.array([]));

    //set userAlarmDefintion
    const items = this.userAlarmDefintionsFormArray;
    items.clear();

    //process the form data after a slight delay
    setTimeout(() => {
      this.processFormData(value);
    }, 0);
  }

  private processFormData(value: IEmailAlertFormResponse) {
    //remove any nulls...wierd bug expierenced where nulls were being added to the array
    value.emailAlertFormItems = value.emailAlertFormItems.filter(
      (item) => item != null,
    );

    value.emailAlertFormItems.forEach((item) => {
      item.alarmOptions = item.alarmOptions.filter((ao) => ao != null);
    });

    value.emailAlertFormItems.forEach((item) => {
      this.addItemToFormArray(item);
    });
    super.formData = value;
    this.formLoaded = true;
    this.changeDetectorRef.markForCheck();
  }

  trackByFn(index: number, item: IEmailAlertFormItem): string {
    const rtn = `${item?.alarmID}:${item?.userId || item?.emailAddress}`;
    return rtn;
  }

  trackAlarmOptionsByFn(index: number, item: IAlarmOption): string {
    return item?.alarmName;
  }

  onResetClick() {
    //remove all "new" items from the FormArray
    const items = this.userAlarmDefintionsFormArray;
    const newItems = this.emailAlertFormItems.filter(
      (item) => item.alarmID === 0,
    );
    newItems.forEach((c) => {
      //remove from form array and emailAlertFormItems
      const index = this.emailAlertFormItems.indexOf(c);
      items.removeAt(index);
      this.emailAlertFormItems.splice(index, 1);
    });
    this.userAndGroupListOptions$.next(this.userAndGroupListOptions);
    this.changeDetectorRef.markForCheck();
  }

  onSaveClick() {
    const valueToSave = this.getAlarmsDefinitionsToSave();
    this.saveClicked.emit(valueToSave);
  }

  private isEmailAlreadyInDb(
    newItem: IEmailAlertFormItem,
  ): Observable<IEmailAlertFormItem | null> {
    //return the new item if the email doesn't exist in the db
    return this._alarmService
      .emailExistsAlreadyInDb(
        this.alarmType,
        this.entityId,
        newItem.emailAddress,
      )
      .pipe(
        map((result) => {
          return newItem;
        }),
        catchError((error) => {
          console.error('Error checking if email exists in db', error);
          return of(null);
        }),
      );
  }

  private getAlarmsDefinitionsToSave(): any {
    //loop through the form array and build the alarms definitions to save
    // grab only the dirty form groups to save
    let alarmsDefinitions = [];
    let formArray = this.userAlarmDefintionsFormArray;

    formArray.controls.forEach((control: FormGroup) => {
      if (control.dirty) {
        //build the alarm definition object to save
        let alarmDefinition = {
          ...control.value,
          alarmOptions: {},
          alarmType: AlarmTypes.Organization,
        };
        const userAlarms = control.get('alarmOptions') as FormArray;
        userAlarms.controls
          .filter((alarmOption: FormGroup) => alarmOption.dirty)
          .forEach((alarmOption: FormGroup) => {
            const key = Object.keys(alarmOption.value)[0];
            alarmDefinition.alarmOptions[key] = alarmOption.value[key];
          });
        alarmsDefinitions.push(alarmDefinition);
      }
    });
    //debugger;
    return alarmsDefinitions;
  }

  alreadyExistsInListValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
      if (!control.value) return null;

      const email = control.value;
      const emailAlreadyExists = this.emailAlertFormItems.some(
        (item) =>
          item.emailAddress.toLowerCase().trim() === email.toLowerCase().trim(),
      );

      return emailAlreadyExists ? { emailAlreadyExists: true } : null;
  };

}

export enum AlarmTypes {
  Tank = 3,
  Organization = 1,
  Location = 2,
}
